// src/components/TransferProgress.js
// At-a-glance recipient progress: Sent → Opened → Code verified → Downloaded / Viewed.
// Earlier steps are inferred from later ones, so older transfers (created before
// opened/code-requested tracking existed) still display sensibly.
import React from 'react';
import { formatDateTime } from '../utils/api';

export function getProgressSteps(t, kind = 'file') {
  const completed = !!t.hasDownloaded || t.status === 'expired-downloaded';
  const viewedOnly = kind === 'file' && !!t.viewOnly;
  const finalDone = viewedOnly ? !!t.viewedAt || completed : completed;
  const verified = !!t.accessedAt || finalDone;
  const opened = !!t.openedAt || verified;

  const finalLabel = kind === 'credential' || viewedOnly ? 'Viewed' : 'Downloaded';
  const finalAt = viewedOnly ? (t.viewedAt || t.downloadedAt) : t.downloadedAt;

  let verifyState = verified ? 'done' : 'pending';
  let verifyNote = '';
  if (!verified && t.codeRequestedAt) { verifyState = 'partial'; verifyNote = 'Code requested'; }

  return [
    {
      key: 'sent', label: t.emailStatus === 'failed' ? 'Email failed' : 'Sent',
      state: t.emailStatus === 'failed' ? 'failed' : 'done', at: t.createdAt,
      extra: [
        t.resendCount > 0 && t.lastResentAt ? `Resent ${t.resendCount}× · last ${formatDateTime(t.lastResentAt)}` : '',
        t.emailSentAs === 'system' ? 'Sent from the shared system mailbox' : '',
      ].filter(Boolean).join(' · '),
      hint: t.emailStatus === 'failed'
        ? 'The transfer was created but the notification email could not be sent.'
        : 'Notification email sent to the recipient.',
    },
    {
      key: 'opened', label: 'Opened', state: opened ? 'done' : 'pending', at: t.openedAt,
      hint: 'Recipient opened the link. Automated email security scanners can occasionally trigger this.',
    },
    {
      key: 'verified', label: 'Code verified', state: verifyState, at: t.accessedAt, note: verifyNote,
      hint: 'Recipient entered the one-time code sent to their email address.',
    },
    {
      key: 'final', label: finalLabel, state: finalDone ? 'done' : 'pending', at: finalAt,
      hint: viewedOnly ? 'Recipient viewed the files (view-only transfers cannot be downloaded).'
        : kind === 'credential' ? 'Recipient viewed the credentials.' : 'Recipient downloaded the files.',
    },
  ];
}

const ICON = { done: '✓', partial: '…', failed: '!', pending: '' };

export default function TransferProgress({ transfer, kind = 'file', horizontal = false }) {
  const steps = getProgressSteps(transfer, kind);
  return (
    <div className={`tp${horizontal ? ' tp-row' : ''}`} role="list" aria-label="Recipient progress">
      {steps.map(s => (
        <div key={s.key} className={`tp-step tp-${s.state}`} role="listitem" title={s.hint}>
          <div className="tp-dot">{ICON[s.state]}</div>
          <div className="tp-text">
            <div className="tp-label">{s.label}</div>
            <div className="tp-time">
              {s.state === 'done' && s.at ? formatDateTime(s.at) : s.note || ''}
            </div>
            {s.extra && <div className="tp-time">{s.extra}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
